<template>
  <div class="flex flex-col gap-3">
    <form-block
      v-model="entity.settings.custom_cta_settings"
      :title="title"
      :description="description"
      :switchable="switchable"
      :disabled="disabled || disabledByAiSettings"
    >
      <info-text v-if="disabledText">
        {{ disabledText }}
      </info-text>
      <info-text v-else-if="entity.isInstagramProfileVisitCampaign">
        {{ tr('You have chosen Instagram profile as the conversion location, so you cannot set a custom CTA.') }}
      </info-text>
      <template v-else>
        <info-text v-if="campaignObjectiveLabel">
          {{ tr('If you do not set a CTA Button URL, no ad will be created from those posts that do not contain a URL, as this is required by the campaign\'s goal (:goal).', { ':goal': campaignObjectiveLabel }) }}
        </info-text>

        <div class="flex flex-col gap-1.5">
          <ui-label>{{ tr('Button type') }}</ui-label>
          <select-field
            v-model="entity.settings.cta_button"
            :options="ctaList"
            :reduce="(obj) => obj.machine_name"
            :aria-label="tr('Button type')"
            :disabled="disabled || disabledByAiSettings"
          />
        </div>

        <template v-if="showCtaUrlInput">
          <div class="flex flex-col gap-1.5">
            <ui-label for="cta-url">{{ tr('Button URL') }}</ui-label>
            <ui-input
              id="cta-url"
              v-model="entity.settings.cta_url"
              :aria-invalid="urlError ? 'true' : null"
              :placeholder="tr('Enter the URL you want to promote')"
              :disabled="disabled || disabledByAiSettings"
              @blur="() => urlValidation()"
            />
          </div>

          <div class="flex flex-col gap-1.5">
            <label :class="[ 'flex w-fit items-center gap-2 text-sm text-foreground', disabled || disabledByAiSettings ? 'cursor-not-allowed' : 'cursor-pointer' ]">
              <ui-checkbox
                :model-value="!!entity.settings.smart_cta_url"
                :disabled="disabled || disabledByAiSettings"
                @update:model-value="(value) => entity.settings.smart_cta_url = value"
              />
              {{ tr('Smart URL') }}
            </label>
            <p class="m-0 text-xs text-muted-foreground">
              {{ tr('If the post contains at least one link, we will use it as the Button URL to create the ad. In this case, the URL provided here will only be used if the post does not contain any links.') }}
            </p>
          </div>
        </template>
      </template>
    </form-block>

    <info-text v-if="switchable && !entity.settings.custom_cta_settings && disabledText">
      {{ disabledText }}
    </info-text>
  </div>
</template>

<script>
import { CTA_URL_USAGE } from '@/consts.js';
import RuleFormBlockMixin from '@/mixins/RuleForm/Block.js';
import InfoText from '@/ui/components/InfoText.vue';
import SelectField from '@/ui/components/SelectField.vue';
import { isValidURL } from '@/utils/validators.js';

export default {
  name: 'CtaBlock',
  components: {
    InfoText,
    SelectField,
  },
  mixins: [ RuleFormBlockMixin ],
  props: {
    switchable: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data: () => ({
    urlError: false,
  }),
  computed: {
    title() {
      if (this.switchable) {
        return this.tr('Custom call to action');
      }

      return this.tr('Call to action');
    },
    description() {
      if (
        this.entity.isLeadFormAd
        || this.entity.isAppPromo
        || this.entity.isInstagramProfileVisitCampaign
      ) {
        return null;
      }

      if (this.disabledText !== null) {
        return null;
      }

      if (this.switchable) {
        return this.tr('By overriding the original ad settings, you can set the CTA that best suits your scaling campaign.')
        + ' '
        + this.tr('You can choose the text for the button from the dropdown list and give the URL you want to promote.');
      }

      return this.tr('If the creative used for the ad doesn\'t have a clickable button to encourage clicks, you can add a CTA button here.')
        + ' '
        + this.tr('You can choose the text for the button from the dropdown list and give the URL you want to promote.')
        + '<br/>'
        + this.tr('(Except Facebook post to Facebook Feed placement.)');
    },
    ctaList() {
      return this.$store.getters['app/facebookCallToActionsByOdax']({
        campaignObjective: this.entity.output_campaign?.objective,
        conversionLocation: this.entity.settings.adset_conversion_location,
        optimizationGoal: this.entity.settings.adset_optimization_goal,
      });
    },
    hasSelectedCtaButton() {
      return this.entity.settings.cta_button !== 'NO_BUTTON';
    },
    ctaUrlUsage() {
      return this.$store.getters['app/facebookOdaxCtaUrlUsage']({
        campaignObjective: this.entity.output_campaign?.objective,
        conversionLocation: this.entity.settings.adset_conversion_location,
      });
    },
    showCtaUrlInput() {
      return this.hasSelectedCtaButton
        && this.ctaUrlUsage !== CTA_URL_USAGE.NOT_USED
        && this.ctaUrlUsage !== CTA_URL_USAGE.APP_STORE;
    },
    campaignObjectiveLabel() {
      // The "no ad without a URL" hint only applies where a clickable destination is required.
      if (this.ctaUrlUsage !== CTA_URL_USAGE.REQUIRED) {
        return null;
      }

      if ([ 'CONVERSIONS', 'OUTCOME_SALES', 'OUTCOME_LEADS' ].includes(this.entity.output_campaign.objective)) {
        return this.tr('Conversion');
      }
      else if ([ 'LINK_CLICKS', 'OUTCOME_TRAFFIC' ].includes(this.entity.output_campaign.objective)) {
        return this.tr('Traffic');
      }

      return null;
    },
    disabledByAiSettings() {
      return this.entity.settings.ai_cta_button
        && (this.entity.settings.ad_variants === 1 || !this.entity.settings.ai_ad_with_original_content);
    },
    disabledText() {
      if (this.disabledByAiSettings) {
        return this.tr('You have entrusted the CTA setup to Infinite∞Ai, so you don\'t have the option to modify it.');
      }

      return null;
    },
  },
  watch: {
    disabledText(val) {
      if (val !== null) {
        if ('custom_cta_settings' in this.entity.settings) {
          this.entity.settings.custom_cta_settings = false;
        }
      }
    },
    'entity.settings.custom_cta_settings'(val) {
      if (!val) {
        this.entity.settings.cta_button = null;
        this.entity.settings.cta_url = '';
        this.entity.settings.smart_cta_url = false;
      }
      else {
        this.entity.settings.cta_button = this.ctaList[0].machine_name;
      }
    },
    'entity.settings.cta_button'(newValue, oldVal) {
      if (newValue && newValue !== 'NO_BUTTON') {
        this.entity.settings.cta_url = this.entity.settings.cta_url || '';
      }
      if (!newValue || newValue === 'NO_BUTTON') {
        this.entity.settings.cta_url = '';
      }
      else if (!oldVal || oldVal === 'NO_BUTTON') {
        this.entity.settings.smart_cta_url = true;
      }
    },
    'entity.settings.cta_url'(val) {
      if (
        (!val || val.trim().length === 0)
        && this.entity.settings.cta_button !== 'NO_BUTTON'
      ) {
        this.entity.settings.smart_cta_url = true;
      }
    },
    showCtaUrlInput: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.entity.settings.smart_cta_url = false;
        }
      },
    },
    ctaList: {
      deep: true,
      immediate: true,
      handler(list) {
        if (!list?.length || list.some(i => i.machine_name === this.entity.settings.cta_button)) {
          return;
        }
        this.entity.settings.cta_button = list[0].machine_name;
      },
    },
  },
  methods: {
    validate() {
      this.urlValidation();

      if (this.showCtaUrlInput && this.urlError !== '') {
        this.error(this.tr('A CTA URL is required if a CTA button is used.'));
        return false;
      }

      return true;
    },
    urlValidation() {
      this.urlError = '';
      if (this.switchable && !this.entity.settings.custom_cta_settings) {
        return;
      }

      if (this.entity.settings.cta_button === 'NO_BUTTON') {
        this.entity.settings.cta_url = '';
        return;
      }

      if (!isValidURL(this.entity.settings.cta_url)) {
        this.urlError = 'error';
        return;
      }

      if (
        this.entity.settings.cta_url
        && this.entity.settings.cta_url.toLowerCase().indexOf('http') !== 0
      ) {
        this.entity.settings.cta_url = 'https://' + this.entity.settings.cta_url;
      }
    },
  },
};
</script>
